import { createAsyncThunk } from "@reduxjs/toolkit";
import { authService } from "../../services/authService";
import {
  CreateTokenDto,
  TwoFactorLoginVerifyRequestDto,
  TwoFactorLoginBackupRequestDto,
  PublicRegistrationFinishExtendedRequestDto,
  TwoFactorVerifyRequestDto,
} from "../../types/api";
import {
  loginStart,
  loginComplete,
  twoFactorStart,
  twoFactorSuccess,
  registrationStart,
  registrationSuccess,
  setRegistrationToken,
  logout,
} from "../slices/authSlice";
import {
  broadcastLogin,
  broadcastLogout,
  clearAuthData,
} from "../../utils/authSync";
import { RootState } from "../store";
import { createAppAsyncThunk } from "../utils";

// Login action
export const login = createAppAsyncThunk(
  "auth/login",
  async (credentials: CreateTokenDto, { dispatch }) => {
    dispatch(loginStart());
    await authService.startTwoFactorLogin(credentials);
    dispatch(loginComplete());
  },
);

// Two-factor authentication action
export const twoFactorAuth = createAppAsyncThunk(
  "auth/twoFactorAuth",
  async (data: TwoFactorLoginVerifyRequestDto, { dispatch }) => {
    dispatch(twoFactorStart());
    const token = await authService.verifyTwoFactorOtp(data);
    const tokenWithTimestamp = {
      ...token,
      issued_at: Date.now() / 1000,
    };

    // Load user data after successful 2FA
    const user = await authService.getCurrentUserWithToken(
      tokenWithTimestamp.access_token,
    );

    dispatch(twoFactorSuccess({ user, token: tokenWithTimestamp }));

    // Broadcast login to other tabs
    broadcastLogin();

    return { user, token: tokenWithTimestamp };
  },
);

// Two-factor backup code authentication action
export const twoFactorBackupAuth = createAppAsyncThunk(
  "auth/twoFactorBackupAuth",
  async (data: TwoFactorLoginBackupRequestDto, { dispatch }) => {
    dispatch(twoFactorStart());
    const token = await authService.verifyTwoFactorBackup(data);
    const tokenWithTimestamp = {
      ...token,
      issued_at: Date.now() / 1000,
    };

    // Load user data after successful backup code auth
    const user = await authService.getCurrentUserWithToken(
      tokenWithTimestamp.access_token,
    );

    dispatch(twoFactorSuccess({ user, token: tokenWithTimestamp }));

    // Broadcast login to other tabs
    broadcastLogin();

    return { user, token: tokenWithTimestamp };
  },
);

// Registration action
export const completeRegistration = createAppAsyncThunk(
  "auth/completeRegistration",
  async (data: PublicRegistrationFinishExtendedRequestDto, { dispatch }) => {
    dispatch(registrationStart());
    await authService.completeRegistration(data);
    dispatch(registrationSuccess());
  },
);

// Set registration token action
export const setRegistrationTokenAction = createAsyncThunk(
  "auth/setRegistrationToken",
  async (token: string, { dispatch }) => {
    dispatch(setRegistrationToken(token));
    return token;
  },
);

// Start registration action
export const startRegistration = createAppAsyncThunk(
  "auth/startRegistration",
  async (email: string) => {
    await authService.startRegistration(email);
  },
);

// Validate registration token action
export const validateRegistrationToken = createAppAsyncThunk(
  "auth/validateRegistrationToken",
  async (token: string) => {
    const isValid = await authService.validateRegistrationToken(token);
    return isValid;
  },
);

// Send registration phone code action
export const sendRegistrationPhoneCode = createAppAsyncThunk(
  "auth/sendRegistrationPhoneCode",
  async (phoneNumber: string) => {
    await authService.sendRegistrationPhoneCode(phoneNumber);
  },
);

// Verify registration phone code action
export const verifyRegistrationPhoneCode = createAppAsyncThunk(
  "auth/verifyRegistrationPhoneCode",
  async (code: string, { dispatch }) => {
    const tokenData = await authService.verifyRegistrationPhoneCode(code);
    const tokenWithTimestamp = {
      ...tokenData,
      issued_at: Date.now() / 1000,
    };

    // Load user data after successful registration phone verification
    const user = await authService.getCurrentUserWithToken(
      tokenWithTimestamp.access_token,
    );

    dispatch(twoFactorSuccess({ user, token: tokenWithTimestamp }));

    // Broadcast login to other tabs
    broadcastLogin();

    return { user, token: tokenWithTimestamp };
  },
);

// Forgot password action
export const forgotPassword = createAppAsyncThunk(
  "auth/forgotPassword",
  async (email: string) => {
    await authService.forgotPassword(email);
  },
);

// Reset password action
export const resetPassword = createAppAsyncThunk(
  "auth/resetPassword",
  async ({
    token,
    password,
    passwordConfirm,
  }: {
    token: string;
    password: string;
    passwordConfirm: string;
  }) => {
    await authService.resetPassword(token, password, passwordConfirm);
  },
);

// Send two factor code action (for setup)
export const sendTwoFactorCode = createAppAsyncThunk(
  "auth/sendTwoFactorCode",
  async (phoneNumber: string) => {
    await authService.sendTwoFactorCode(phoneNumber);
  },
);

// Resend login two factor code action
export const resendLoginTwoFactorCode = createAppAsyncThunk(
  "auth/resendLoginTwoFactorCode",
  async (email: string) => {
    await authService.resendLoginTwoFactorCode(email);
  },
);

// Verify two factor setup action
export const verifyTwoFactorSetup = createAppAsyncThunk(
  "auth/verifyTwoFactorSetup",
  async (data: TwoFactorVerifyRequestDto) => {
    const codes = await authService.verifyTwoFactorSetup(data);
    return codes;
  },
);

// Regenerate backup codes action
export const regenerateBackupCodes = createAppAsyncThunk(
  "auth/regenerateBackupCodes",
  async () => {
    const codes = await authService.regenerateBackupCodes();
    return codes;
  },
);

// Logout action
// Note: This action clears local state even if API call fails
export const logoutAction = createAppAsyncThunk(
  "auth/logoutAction",
  async (_, { dispatch, getState }) => {
    try {
      const state = getState() as RootState;
      const token = state.auth.token;

      // Call API to delete token on server
      if (token?.refresh_token) {
        await authService.logout({ refresh_token: token.refresh_token });
      }

      // Clear auth data and dispatch logout
      clearAuthData();
      dispatch(logout());

      // Broadcast logout to other tabs
      broadcastLogout();
    } catch (error: unknown) {
      // Even if API call fails, clear local state
      clearAuthData();
      dispatch(logout());
      broadcastLogout();

      // Re-throw to let createAppAsyncThunk handle error logging
      throw error;
    }
  },
);
